import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  centsFromDecimal,
  formatCivilDate,
  formatCurrency,
  resolveFormattingLocale,
  upgradeRefusalOf,
  useCheckUpgrade,
  useUpgrade,
  useUpgradeTargets,
  type CivilDate,
  type UpgradeCheckResult,
  type UpgradeKind,
} from '@eis/core';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { EffectiveDateSelector } from '@eis/patterns';
import { ConflictNotice } from './ConflictNotice';

/**
 * "Tarifstand aktualisieren" — the upgrade to a newer tariff level (SDD-016 §5), on the policy AND
 * the group policy detail.
 *
 * The operator picks a target (PUBLISHED or ACTIVE, newer, same tarif and generation — the service
 * lists them), a date, and presses "Prüfen": the dry run lists EVERY failed check with its path and
 * shows the premium before and after. Only a passing check for exactly that target and date
 * enables the confirm, which writes ONE endorsement from the chosen date; earlier periods stay as
 * they were. A group upgrade moves no member — the dialog says so.
 *
 * Operations vocabulary on purpose (SDD-008 D-C): "Tarifstand" / "tariff level", never the word
 * the Produkte screens use — the number is shown, the configuration jargon is not.
 */
export function UpgradeDialog({
  kind,
  code,
  currentVersionErn,
  open,
  ready,
  onClose,
}: {
  readonly kind: UpgradeKind;
  readonly code: string;
  /** The pin today — its number is what "current" shows. */
  readonly currentVersionErn: string;
  readonly open: boolean;
  /** False while the instance is being re-read: the confirm quotes the ETag that read restores. */
  readonly ready: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const targets = useUpgradeTargets(kind, code, open);
  const check = useCheckUpgrade();
  const upgrade = useUpgrade();
  const [target, setTarget] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState<CivilDate | null>(null);
  const [checkedFor, setCheckedFor] = useState('');

  const selection = `${target}|${effectiveFrom ?? ''}`;
  const result: UpgradeCheckResult | undefined = checkedFor === selection ? check.data : undefined;
  const refusal = upgradeRefusalOf(upgrade.error);
  const shownIssues = refusal?.issues ?? result?.issues ?? [];
  const options = targets.data ?? [];

  const close = (): void => {
    setTarget('');
    setEffectiveFrom(null);
    setCheckedFor('');
    check.reset();
    upgrade.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="upgrade-title">
      <DialogTitle id="upgrade-title">{t('policies.upgrade.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('policies.upgrade.current', { number: numberOf(currentVersionErn) })}
          </Typography>
          <Alert severity="info">
            {t('policies.upgrade.hint')}
            {kind === 'group' ? ` ${t('policies.upgrade.groupHint')}` : ''}
          </Alert>

          {targets.isSuccess && options.length === 0 ? (
            <Alert severity="info">{t('policies.upgrade.none')}</Alert>
          ) : (
            <TextField
              select
              size="small"
              label={t('policies.upgrade.target')}
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
              }}
              disabled={targets.isPending}
            >
              {options.map((option) => (
                <MenuItem key={option.versionErn} value={option.versionErn ?? ''}>
                  {option.active === true
                    ? t('policies.upgrade.targetOffered', { number: option.number })
                    : t('policies.upgrade.targetOption', { number: option.number })}
                </MenuItem>
              ))}
            </TextField>
          )}

          <EffectiveDateSelector
            label={t('policies.upgrade.effectiveFrom')}
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            required
            locale={locale}
          />

          <ConflictNotice conflict={upgrade.conflict} />

          {result?.ok === true && (
            <Alert severity="success">
              <AlertTitle>{t('policies.upgrade.checkOk')}</AlertTitle>
              {result.major === true ? t('policies.upgrade.majorHint') : null}
            </Alert>
          )}
          {result !== undefined && (
            <PremiumLines
              before={result.premiumBefore}
              after={result.premiumAfter}
              locale={locale}
            />
          )}
          {shownIssues.length > 0 && (
            <Alert severity="error">
              <AlertTitle>
                {refusal !== undefined
                  ? t('policies.upgrade.refused')
                  : t('policies.upgrade.checkFailed', { count: shownIssues.length })}
              </AlertTitle>
              <List dense disablePadding>
                {shownIssues.map((issue, index) => (
                  <ListItem
                    key={`${issue.check ?? ''}-${issue.path ?? ''}-${String(index)}`}
                    disableGutters
                  >
                    <ListItemText
                      primary={t(`policies.upgrade.checkName.${issue.check ?? ''}` as never)}
                      secondary={`${issue.path !== undefined && issue.path !== '' ? `${issue.path} · ` : ''}${issue.message ?? ''}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          {upgrade.isSuccess && effectiveFrom !== null && (
            <Alert severity="success">
              {t('policies.upgrade.done', { date: formatCivilDate(effectiveFrom, locale) })}
            </Alert>
          )}
          {check.isError && <Alert severity="error">{check.error.message}</Alert>}
          {upgrade.isError && upgrade.conflict === null && refusal === undefined && (
            <Alert severity="error">{upgrade.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.upgrade.cancel')}</Button>
        <Button
          variant="outlined"
          disabled={target === '' || effectiveFrom === null || check.isPending}
          onClick={() => {
            if (effectiveFrom === null) return;
            setCheckedFor(selection);
            check.mutate({ kind, code, target, effectiveFrom });
          }}
        >
          {t('policies.upgrade.check')}
        </Button>
        <Button
          variant="contained"
          // Only after a passing check for exactly this target and date, and once the re-read has
          // restored the precondition the write quotes.
          disabled={result?.ok !== true || !ready || upgrade.isPending || upgrade.isSuccess}
          onClick={() => {
            if (effectiveFrom === null) return;
            upgrade.mutate({ kind, code, target, effectiveFrom });
          }}
        >
          {t('policies.upgrade.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PremiumLines({
  before,
  after,
  locale,
}: {
  readonly before: InstanceApi.Money | undefined;
  readonly after: InstanceApi.Money | undefined;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  if (before === undefined && after === undefined) return null;
  return (
    <Stack direction="row" spacing={3}>
      <Typography variant="body2">
        {t('policies.upgrade.premiumBefore')}: {money(before, locale)}
      </Typography>
      <Typography variant="body2">
        {t('policies.upgrade.premiumAfter')}: {money(after, locale)}
      </Typography>
    </Stack>
  );
}

function money(amount: InstanceApi.Money | undefined, locale: string): string {
  const value = amount?.amount ?? '';
  const currency = amount?.currency ?? '';
  if (value === '') return '—';
  const cents = centsFromDecimal(value);
  if (cents === null) return currency === '' ? value : `${value} ${currency}`;
  return formatCurrency(cents, currency === '' ? 'EUR' : currency, locale);
}

/** `tarifservice:fahrrad/2026/1.3` → `1.3`. */
function numberOf(ern: string): string {
  return ern.slice(ern.lastIndexOf('/') + 1);
}
